/**
 * The map's two palettes, and the switch that follows the CRM's theme.
 *
 * ⚠ THE COLOURS ARE LITERAL HEX, not tokens, and that is not laziness. The
 * markers are handed to Google, which renders them inside its own overlay
 * subtree; a custom property that fails to resolve there comes out as an empty
 * string and paints nothing at all. These values are the CRM's own tokens
 * converted once: `#4163e4` is `oklch(0.55 0.20 268)`, the brand indigo, and
 * `#7197ff` is the same hue lifted so it still reads on a dark map.
 *
 * ⚠ NO CUSTOM JSON MAP STYLE, and it is not an oversight. Google's own
 * documentation on `MapOptions.styles`: "This feature is not available when
 * using a map ID, or when using vector maps." The map needs a map ID because
 * `AdvancedMarkerElement` requires one, and that is what carries the HTML labels
 * on every pin. So the dark map comes from `colorScheme`, which does work
 * alongside a map ID. A hand-written palette would have to be uploaded to the
 * Google Cloud console as a styled map ID and referenced by id instead.
 */

/** What one theme paints. */
export interface MapPalette {
  /** `colorScheme` for `MapOptions`. Google accepts the plain string. */
  scheme: "LIGHT" | "DARK";
  /** A company worth calling. The CRM's brand indigo, never the old amber. */
  lead: string;
  /** Everyone else. */
  plain: string;
  /** The name chip behind a pin's label, and its text. */
  chipBg: string;
  chipFg: string;
  /** The ring around the pin whose card is open. */
  ring: string;
  /** The hairline every marker draws to lift itself off the map. */
  edge: string;
}

export const MAP_PALETTE: Record<"light" | "dark", MapPalette> = {
  light: {
    scheme: "LIGHT",
    lead: "#4163e4",
    plain: "#777a82",
    chipBg: "rgba(255,255,255,0.94)",
    chipFg: "#0f172a",
    ring: "#4163e4",
    edge: "#ffffff",
  },
  dark: {
    // Lifted, not the same indigo: `#4163e4` on Google's dark cartography is a
    // dot the eye slides over. The pin has to be the brightest thing on a dark
    // map, which is the opposite of what it has to be on a light one.
    scheme: "DARK",
    lead: "#7197ff",
    plain: "#9fa4b2",
    chipBg: "rgba(19,22,29,0.92)",
    chipFg: "#e8eaf0",
    ring: "#7197ff",
    // The rim is what separates a pin from the map underneath it. White works
    // on a light map; on a dark one the same white is a halo, so the rim is the
    // shell's own near-black instead.
    edge: "#13161d",
  },
};

/** True when the CRM is in dark mode. The class on `<html>` is the source of
 *  truth - `themeMode.ts` puts it there, resolving "system" on the way. */
export function isDarkTheme(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

export const currentPalette = (): MapPalette => MAP_PALETTE[isDarkTheme() ? "dark" : "light"];

/**
 * Calls back whenever the CRM's theme flips, in either direction.
 *
 * Watches the class attribute rather than `prefers-color-scheme`, because the
 * operator's own light/dark choice never touches the media query - only the
 * class. Watching the query alone would leave the map light while the rest of
 * the CRM went dark.
 */
export function watchTheme(onChange: (dark: boolean) => void): () => void {
  if (typeof document === "undefined") return () => {};
  let last = isDarkTheme();
  const observer = new MutationObserver(() => {
    const now = isDarkTheme();
    if (now === last) return;
    last = now;
    onChange(now);
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}
